import { Button } from 'antd';
import { ARRANGEMENT_KINDS, type ArrangementSearchHintFilter, type ArrangementType } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import {
  ARRANGEMENT_KIND_LABEL,
  LIST_FILTERS_COPY,
  SEARCH_HINT_COPY,
  SEARCH_HINT_FILTER_LABEL,
  TYPED_TAB_COPY,
} from '../copy';
import { orderTypeLabel, sessionFilterNotes, type SessionFilterQuery } from '../listFilters';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5b §6] WHAT THE ORDERS TABLE SAYS WHEN IT IS EMPTY — FOUR CASES, TWO OF THEM ACTIONABLE.
 *
 *  0. ⚠️ [LIST-FILTERS] NO SEARCH, AND "ADJUST FILTERS" WAS ON: the page says the filters hid
 *     everything and offers ONE action, "Clear filters". "No arrangements yet." over a list the
 *     operator narrowed to one division is false, and it sends them to raise an order that may
 *     already exist. It reads the page's own `filteredBy` tag — what the request SENT — never the
 *     live store: `keepPreviousData` keeps this page on screen while a cleared filter's request is in
 *     flight, and the store would flash the unfiltered sentence over a page that is still filtered.
 *  1. NO SEARCH: the page's own sentence, unchanged. An empty list with no term is the filters
 *     working as asked, and the server computes no hint for it.
 *  2. A HINT: the search found nothing because the filters it names hid the matches — inside the
 *     caller's own scope, which the server never relaxes. ONE action, "Show matches", clears exactly
 *     those filters and keeps the term: `setFilters(searchHintPatch(keys))`.
 *  3. OTHERWISE: which tab, and what the search reads — and NO action, because there is nothing to
 *     widen that the server found. ⚠️ `kind` is a tab and is never offered as a reset; the sentence
 *     names it instead.
 *
 *  ⚠️ THE TERM IS THE ONE THE PAGE WAS FETCHED FOR (`searchedFor`), NEVER THE SEARCH BOX'S. The list
 *  keeps the previous page on screen while the next one loads (`keepPreviousData`), so reading the
 *  box would pair a new term with the previous term's hint.
 *
 *  ⚠️ [LIST-FILTERS] WITH A TERM, CASES 2 AND 3 ARE UNCHANGED UNDER A SESSION FILTER — the server
 *  probes the division too, so a hint names "the division filter" when it is what hid the matches,
 *  and the no-hint sentence's "with these filters" stays true.
 *
 *  ⚠️ [TYPE-FILTER] EXCEPT A NARROWING "ORDER TYPE", WHICH THE SERVER NEVER NAMES (on Durga Puja PA it is
 *  the route). Case 3 under one says so and offers ONE action, "Show all types": it widens the type to
 *  All and keeps the term. It promises no match — the server did not probe for one — only the widening.
 *  Case 0 says which filters hid everything, one sentence each, from what the page SENT.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** What the page's `queryFn` returns beside its rows — the hint, and the term that produced it. */
export type SearchEmptyPage = {
  readonly searchedFor?: string;
  readonly searchHint?: { readonly resetFilters: readonly ArrangementSearchHintFilter[] };
  /** [LIST-FILTERS] The session filter ("Adjust filters") the request carried — what was SENT; absent for none. */
  readonly filteredBy?: SessionFilterQuery;
};

type SearchEmptyStateProps = {
  page: SearchEmptyPage | undefined;
  /** The tab (`kind`) as the page reads it from the URL. */
  kind: string;
  /**
   * ⚠️ [P7B §5] The typed tab's label (`Durga Puja PA`) when that tab is on screen, else `undefined`.
   * It takes precedence over `kind`: a typed order is `STANDARD`, so the kind's sentence would name
   * the wrong tab — and the no-search sentence would point at a button the typed tab does not offer.
   */
  typeLabel?: string | undefined;
  /**
   * [DP2026] REQUIRED, from the one caller: the typed list's type, or `undefined` on Manage Arrangements. The
   * booklet note names the type's own year, and a default would drop that note silently.
   */
  fixedType: ArrangementType | undefined;
  onReveal: (filters: readonly ArrangementSearchHintFilter[]) => void;
  /**
   * ⚠️ [LIST-FILTERS] REQUIRED, from the one caller: "Clear filters" on a page the session filter
   * emptied. A default would ship an empty state whose only action does nothing.
   */
  onClearFilters: () => void;
  /**
   * [TYPE-FILTER] REQUIRED, from the one caller: "Show all types" after an empty search under a narrowing type, with
   * no hint — it promises no match, only the widening.
   */
  onShowAllTypes: () => void;
};

/** "the dates and the status" — the platform's own list grammar, not a hand-rolled join. */
const FILTER_LIST = new Intl.ListFormat('en', { type: 'conjunction' });

export const SearchEmptyState = ({
  page,
  kind,
  typeLabel,
  fixedType,
  onReveal,
  onClearFilters,
  onShowAllTypes,
}: SearchEmptyStateProps) => {
  const term = page?.searchedFor;
  const filteredBy = page?.filteredBy;
  if ((term === undefined || term === '') && filteredBy !== undefined) {
    return (
      <EmptyState
        size="sm"
        title={typeLabel === undefined ? LIST_FILTERS_COPY.emptyTitle : LIST_FILTERS_COPY.typedEmptyTitle(typeLabel)}
        description={sessionFilterNotes(filteredBy, fixedType).join(' ')}
        action={
          <Button data-testid="list-filters-empty-clear" onClick={onClearFilters}>
            {LIST_FILTERS_COPY.clear}
          </Button>
        }
      />
    );
  }
  if (term === undefined || term === '') {
    return typeLabel === undefined ? (
      <EmptyState size="sm" title={SEARCH_HINT_COPY.emptyTitle} description={SEARCH_HINT_COPY.emptyBody} />
    ) : (
      <EmptyState size="sm" title={TYPED_TAB_COPY.emptyTitle(typeLabel)} description={TYPED_TAB_COPY.emptyBody} />
    );
  }

  const resetFilters = page?.searchHint?.resetFilters;
  if (resetFilters !== undefined && resetFilters.length > 0) {
    return (
      <EmptyState
        size="sm"
        title={SEARCH_HINT_COPY.hiddenTitle(term)}
        description={SEARCH_HINT_COPY.hiddenBody(
          FILTER_LIST.format(resetFilters.map((filter) => SEARCH_HINT_FILTER_LABEL[filter])),
        )}
        action={
          <Button
            data-testid="search-hint-reveal"
            onClick={() => {
              onReveal(resetFilters);
            }}
          >
            {SEARCH_HINT_COPY.reveal}
          </Button>
        }
      />
    );
  }

  if (typeLabel !== undefined) {
    return (
      <EmptyState size="sm" title={TYPED_TAB_COPY.noneTitle(typeLabel, term)} description={SEARCH_HINT_COPY.noneBody} />
    );
  }

  /* A hand-edited `?kind=` the server refused would never reach here; `STANDARD` is its default. */
  const tab = ARRANGEMENT_KINDS.find((each) => each === kind) ?? 'STANDARD';
  const narrowedType = filteredBy?.type;
  return (
    <EmptyState
      size="sm"
      title={SEARCH_HINT_COPY.noneTitle(ARRANGEMENT_KIND_LABEL[tab].toLowerCase(), term)}
      description={
        narrowedType === undefined
          ? SEARCH_HINT_COPY.noneBody
          : `${SEARCH_HINT_COPY.noneBody} ${LIST_FILTERS_COPY.typeSet(orderTypeLabel(narrowedType))}`
      }
      action={
        narrowedType === undefined ? undefined : (
          <Button data-testid="list-filters-all-types" onClick={onShowAllTypes}>
            {LIST_FILTERS_COPY.showAllTypes}
          </Button>
        )
      }
    />
  );
};
